import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronLeft, Layers, Info, Search, RefreshCw, Tag, Hash, DollarSign, Grid3x3
} from 'lucide-react';
import { fetchApartmentUnits, fetchUnitGroups } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';
import GroupUnitsDialog from './GroupUnitsDialog';

/**
 * AllUnitsView
 * ─────────────
 * Props:
 *   currentApartment  – the full apartment object (has .categories[])
 *   onBack            – fn called to navigate back to categories
 */
const AllUnitsView = ({ currentApartment, onBack }) => {
  const { palette, Colors } = useAppTheme();
  const [units, setUnits]           = useState([]);
  const [groups, setGroups]         = useState({});   // categoryId → group[]
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showGroupDialog, setShowGroupDialog] = useState(false);

  const P   = Colors.primary.main;
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;

  // ── data loading ──────────────────────────────────────────────────────────

  const loadUnits = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchApartmentUnits(currentApartment.id);
      setUnits(data);
    } catch (err) {
      setError('Failed to load units: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    // fetch groups for every category that has units
    const categoryIds = [...new Set(units.map(u => u.categoryId))];
    const results = {};
    await Promise.all(
      categoryIds.map(async (cid) => {
        try {
          const grps = await fetchUnitGroups(cid);
          results[cid] = grps;
        } catch (_) {
          results[cid] = [];
        }
      })
    );
    setGroups(results);
  };

  useEffect(() => {
    if (currentApartment) loadUnits();
  }, [currentApartment]);

  useEffect(() => {
    if (units.length > 0) loadGroups();
  }, [units]);

  // ── filtering + ordering ──────────────────────────────────────────────────

  const orderedUnits = useMemo(() => {
    // Build a sorted flat list respecting group order, then ungrouped at end per category
    const grouped = [];
    const ungrouped = [];

    // For each category, collect group memberships in order
    const categoryIds = [...new Set(units.map(u => u.categoryId))];

    categoryIds.forEach(cid => {
      const catGroups = (groups[cid] || []).slice().sort((a, b) => a.order - b.order || a.id - b.id);
      const assignedIds = new Set();

      catGroups.forEach(grp => {
        const grpUnits = (grp.units || [])
          .map(gu => units.find(u => u.id === gu.id))
          .filter(Boolean);
        grpUnits.forEach(u => { assignedIds.add(u.id); grouped.push(u); });
      });

      // units in this category not in any group
      units
        .filter(u => u.categoryId === cid && !assignedIds.has(u.id))
        .sort((a, b) => (a.apartmentUnitId || 0) - (b.apartmentUnitId || 0))
        .forEach(u => ungrouped.push(u));
    });

    return [...grouped, ...ungrouped];
  }, [units, groups]);

  const filteredUnits = useMemo(() => {
    return orderedUnits.filter(u => {
      const matchesSearch =
        !search ||
        u.number.toLowerCase().includes(search.toLowerCase()) ||
        u.categoryName.toLowerCase().includes(search.toLowerCase()) ||
        (u.groupName || '').toLowerCase().includes(search.toLowerCase());
      const matchesCat =
        filterCategory === 'all' || String(u.categoryId) === String(filterCategory);
      return matchesSearch && matchesCat;
    });
  }, [orderedUnits, search, filterCategory]);

  // ── helpers ───────────────────────────────────────────────────────────────

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency', currency: 'KES',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount);

  const statusStyle = (status) => {
    const map = {
      Occupied:    { bg: `${Colors.status.success}18`, color: Colors.status.success },
      Vacant:      { bg: `${P}18`,                     color: P },
      Maintenance: { bg: `${Colors.status.warning}18`, color: Colors.status.warning },
      Reserved:    { bg: `${Colors.accent.purple}18`,  color: Colors.accent.purple },
    };
    return map[status] || { bg: `${txt.muted}18`, color: txt.muted };
  };

  const uniqueCategories = [...new Map(units.map(u => [u.categoryId, u.categoryName])).entries()];

  const inputSt = {
    padding: '8px 12px',
    border: `1px solid ${bdr}`,
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: bg.tertiary,
    color: txt.primary,
    fontSize: '14px',
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg.primary }}>

      {/* ── Header ── */}
      <div className="rounded-xl shadow-sm p-6 mb-6"
        style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
        <button onClick={onBack}
          className="flex items-center gap-2 mb-4 font-medium transition group px-3 py-1.5 rounded-md"
          style={{ color: txt.secondary, border: `1px solid ${bdr}` }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
          <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Back to Categories
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold mb-1" style={{ color: P }}>
              All Units — {currentApartment.name}
            </h2>
            <p className="text-sm" style={{ color: txt.secondary }}>
              {units.length} unit{units.length !== 1 ? 's' : ''} across{' '}
              {uniqueCategories.length} categor{uniqueCategories.length !== 1 ? 'ies' : 'y'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={loadUnits} disabled={loading}
              className="p-2 rounded-lg transition"
              style={{ border: `1px solid ${bdr}`, color: txt.secondary, backgroundColor: bg.tertiary }}
              title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowGroupDialog(true)}
              className="flex items-center gap-2 font-semibold px-4 py-2 rounded-lg text-white transition shadow-sm"
              style={{ backgroundColor: Colors.accent.purple }}>
              <Layers className="w-4 h-4" />
              Group Units
            </button>
          </div>
        </div>
      </div>

      {/* ── Informational banner ── */}
      <div className="rounded-xl p-4 mb-6 flex items-start gap-3"
        style={{ backgroundColor: `${P}08`, border: `1px solid ${P}25` }}>
        <Info className="w-4 h-4 mt-0.5 shrink-0" style={{ color: P }} />
        <p className="text-sm" style={{ color: txt.secondary }}>
          This page shows all units in read-only mode.
          To edit rent amounts, unit names, or any unit-level detail —{' '}
          <button onClick={onBack} className="font-semibold underline underline-offset-2"
            style={{ color: P }}>navigate back</button>{' '}
          and select a specific category.
        </p>
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: txt.muted }} />
          <input
            type="text"
            placeholder="Search by unit name, category or group…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputSt, paddingLeft: '36px', width: '100%' }}
          />
        </div>

        {/* Category filter */}
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          style={{ ...inputSt, minWidth: '180px' }}>
          <option value="all">All Categories</option>
          {uniqueCategories.map(([id, name]) => (
            <option key={id} value={String(id)}>{name}</option>
          ))}
        </select>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="rounded-lg p-4 mb-4"
          style={{ backgroundColor: `${Colors.status.error}15`, border: `1px solid ${Colors.status.error}40`, color: Colors.status.error }}>
          {error}
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="text-center py-16 rounded-xl"
          style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3"
            style={{ borderColor: P, borderTopColor: 'transparent' }} />
          <p style={{ color: txt.secondary }}>Loading units…</p>
        </div>
      ) : filteredUnits.length === 0 ? (
        <div className="text-center py-16 rounded-xl"
          style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          <Grid3x3 className="w-12 h-12 mx-auto mb-3" style={{ color: txt.muted }} />
          <p className="font-semibold text-lg" style={{ color: txt.primary }}>No units found</p>
          <p className="text-sm mt-1" style={{ color: txt.secondary }}>Try adjusting your search or filter.</p>
        </div>
      ) : (
        <div className="rounded-xl shadow-sm overflow-hidden"
          style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: bg.tertiary, borderBottom: `1px solid ${bdr}` }}>
                  {[
                    { icon: <Hash className="w-3.5 h-3.5" />,         label: 'ID' },
                    { icon: <Tag className="w-3.5 h-3.5" />,          label: 'Unit Name' },
                    { icon: <Grid3x3 className="w-3.5 h-3.5" />,      label: 'Category' },
                    { icon: <Layers className="w-3.5 h-3.5" />,       label: 'Group' },
                    { icon: <DollarSign className="w-3.5 h-3.5" />,   label: 'Monthly Rent' },
                    { icon: null,                                       label: 'Status' },
                  ].map(({ icon, label }) => (
                    <th key={label}
                      className="px-5 py-3.5 text-left font-semibold"
                      style={{ color: txt.secondary, whiteSpace: 'nowrap' }}>
                      <span className="inline-flex items-center gap-1.5">
                        {icon}{label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUnits.map((unit, idx) => {
                  const { bg: sBg, color: sColor } = statusStyle(unit.status);
                  return (
                    <tr key={unit.id}
                      style={{
                        borderBottom: `1px solid ${bdr}`,
                        backgroundColor: idx % 2 === 0 ? bg.secondary : bg.tertiary + '55',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = `${P}06`}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? bg.secondary : bg.tertiary + '55'}>

                      {/* ID */}
                      <td className="px-5 py-3.5">
                        <span className="font-mono text-xs px-2 py-1 rounded"
                          style={{ backgroundColor: bg.tertiary, color: txt.muted }}>
                          #{unit.id}
                        </span>
                      </td>

                      {/* Unit Name */}
                      <td className="px-5 py-3.5 font-semibold" style={{ color: txt.primary }}>
                        {unit.number}
                      </td>

                      {/* Category */}
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${P}15`, color: P }}>
                          {unit.categoryName}
                        </span>
                      </td>

                      {/* Group */}
                      <td className="px-5 py-3.5">
                        {unit.groupName ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${Colors.accent.purple}15`, color: Colors.accent.purple }}>
                            <Layers className="w-3 h-3" />{unit.groupName}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: txt.muted }}>—</span>
                        )}
                      </td>

                      {/* Rent */}
                      <td className="px-5 py-3.5 font-semibold" style={{ color: txt.primary }}>
                        {formatCurrency(unit.rent)}
                      </td>

                      {/* Status */}
                      <td className="px-5 py-3.5">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold"
                          style={{ backgroundColor: sBg, color: sColor }}>
                          {unit.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer count */}
          <div className="px-5 py-3 text-xs" style={{ color: txt.muted, borderTop: `1px solid ${bdr}` }}>
            Showing {filteredUnits.length} of {units.length} units
          </div>
        </div>
      )}

      {/* ── Group Units Dialog ── */}
      {showGroupDialog && (
        <GroupUnitsDialog
          currentApartment={currentApartment}
          onClose={() => setShowGroupDialog(false)}
          onSaved={() => {
            setShowGroupDialog(false);
            loadUnits();
          }}
        />
      )}
    </div>
  );
};

export default AllUnitsView;